import {
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { RHFTextField } from 'src/components/hook-form';
import RHFMarkdownEditor from 'src/components/markdown-editor/markdownEditor';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import {
  getTeleHealthTherapyDetails,
  updateTeleHealthTherapy,
  getTeleHealthTherapyCategories,
} from 'src/services/telehealth.service';
import { searchConcerns } from 'src/services/concerns.service';
import { searchProducts } from 'src/services/products.service';
import { getBodyParts } from 'src/services/treatments.service';

export default function ClinicEditTeleHealthView() {
  const { user } = useAuthContext();
  const { therapyId } = useParams();
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [therapyCategories, setTherapyCategories] = useState([]);
  const [bodyPartsOptions, setBodyPartsOptions] = useState([]);

  const [concernOptions, setConcernOptions] = useState([]);
  const [concernInput, setConcernInput] = useState('');
  const concernDebounce = useRef(null);

  const [productOptions, setProductOptions] = useState([]);
  const [productInput, setProductInput] = useState('');
  const productDebounce = useRef(null);

  const schema = Yup.object().shape({
    therapyCategory: Yup.number().required(),
    therapy_name: Yup.string().required(),
    description: Yup.string(),
    how_it_works: Yup.string(),
    expected_outcomes: Yup.array().of(Yup.string()),
    min_duration_months: Yup.number().min(1).required(),
    max_duration_months: Yup.number().min(1).required(),
    body_parts: Yup.array().of(Yup.string()),
    concern_ids: Yup.array().of(Yup.string()),
    products: Yup.array().of(Yup.string()),
    prescription_required: Yup.boolean(),
    therapy_images: Yup.array().of(Yup.string()),
    promo_image: Yup.string().nullable(),
  });

  const methods = useForm({ resolver: yupResolver(schema) });
  const { control, watch, setValue, handleSubmit, reset } = methods;

  const promoImage = watch('promo_image');
  const therapyImages = watch('therapy_images');

  useEffect(() => {
    getTeleHealthTherapyCategories().then((res) =>
      setTherapyCategories((res || []).map((c) => ({ value: c.value, label: c.label })))
    );
    getBodyParts().then((res) => setBodyPartsOptions(res || []));
  }, []);

  useEffect(() => {
    getTeleHealthTherapyDetails(therapyId, user.clinic_id).then((therapy) => {
      if (!therapy) return;

      reset({
        therapyCategory: therapy.therapyCategory,
        therapy_name: therapy.therapy_name,
        description: therapy.description || '',
        how_it_works: therapy.how_it_works || '',
        expected_outcomes: therapy.expected_outcomes || [],
        min_duration_months: therapy.min_duration_months,
        max_duration_months: therapy.max_duration_months,
        body_parts: therapy.body_parts || [],
        concern_ids: (therapy.concern_ids || []).map((c) => c.id),
        products: (therapy.products || []).map((p) => p.id),
        prescription_required: therapy.prescription_required || false,
        therapy_images: therapy.therapy_images || [],
        promo_image: therapy.promo_image || '',
      });

      setConcernOptions((prev) => {
        const map = new Map((prev || []).map((c) => [c.id, c]));
        (therapy.concern_ids || []).forEach((c) => map.set(c.id, c));
        return Array.from(map.values());
      });

      setProductOptions((prev) => {
        const map = new Map((prev || []).map((p) => [p.id, p]));
        (therapy.products || []).forEach((p) => map.set(p.id, p));
        return Array.from(map.values());
      });
    });
  }, [therapyId, user.clinic_id, reset]);

  // Debounced concern search
  useEffect(() => {
    if (concernDebounce.current) clearTimeout(concernDebounce.current);
    if (concernInput.length >= 2) {
      concernDebounce.current = setTimeout(() => {
        searchConcerns(concernInput).then((res) => {
          setConcernOptions((prev) => {
            const map = new Map((prev || []).map((c) => [c.id, c]));
            (res || []).forEach((c) => map.set(c.id, c));
            return Array.from(map.values());
          });
        });
      }, 300);
    }
    return () => concernDebounce.current && clearTimeout(concernDebounce.current);
  }, [concernInput]);

  // Debounced product search
  useEffect(() => {
    if (productDebounce.current) clearTimeout(productDebounce.current);
    if (productInput.length >= 2) {
      productDebounce.current = setTimeout(() => {
        searchProducts(productInput).then((res) => {
          setProductOptions((prev) => {
            const map = new Map((prev || []).map((p) => [p.id, p]));
            (res || []).forEach((p) => map.set(p.id, p));
            return Array.from(map.values());
          });
        });
      }, 300);
    }
    return () => productDebounce.current && clearTimeout(productDebounce.current);
  }, [productInput]);

  const onSubmit = (data) => {
    updateTeleHealthTherapy(therapyId, { ...data, clinic_id: user.clinic_id })
      .then(() => {
        setSnackbarMessage('Tele health therapy updated successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        navigate(-1);
      })
      .catch((e) => {
        setSnackbarMessage(e.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  return (
    <Container sx={{ background: 'white', m: 4, p: 3, maxWidth: 840, borderRadius: 4 }}>
      <Stack direction="row" gap={2} alignItems="center" mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Edit Tele Health Therapy</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="therapy_name" label="Therapy Name" InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="therapyCategory"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={therapyCategories || []}
                    getOptionLabel={(o) => o.label}
                    value={therapyCategories.find((c) => c.value === field.value) || null}
                    onChange={(_, v) => field.onChange(v?.value || null)}
                    renderInput={(p) => <TextField {...p} label="Therapy Category" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RHFMarkdownEditor name="description" />
            </Grid>

            <Grid item xs={12}>
              <RHFMarkdownEditor name="how_it_works" />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="min_duration_months" type="number" label="Min Duration" InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFTextField name="max_duration_months" type="number" label="Max Duration" InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="expected_outcomes"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={Array.isArray(field.value) ? field.value : []}
                    onChange={(_, v) => field.onChange(v)}
                    renderTags={(v, getTagProps) =>
                      v.map((o, i) => (
                        <Chip key={i} label={o} {...getTagProps({ index: i })} />
                      ))
                    }
                    renderInput={(p) => <TextField {...p} label="Expected Outcomes" />}
                  />
                )}
              />

            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="body_parts"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={bodyPartsOptions || []}
                    value={field.value || []}
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(p) => <TextField {...p} label="Body Parts" />}
                    getOptionLabel={(o) => o || ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="concern_ids"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={concernOptions || []}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(o) => o.name || ''}
                    filterOptions={(x) => x}
                    inputValue={concernInput}
                    onInputChange={(_, v, reason) => {
                      if (reason === 'input') setConcernInput(v);
                      if (reason === 'clear') setConcernInput('');
                    }}
                    value={(concernOptions || []).filter((c) =>
                      (field.value || []).includes(c.id)
                    )}
                    onChange={(_, v) => {
                      field.onChange(v.map((x) => x.id));
                      setConcernInput('');
                    }}
                    renderInput={(p) => <TextField {...p} label="Concerns" />}
                  />
                )}
              />
            </Grid>


            <Grid item xs={12} lg={6}>
              <Controller
                name="products"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={productOptions || []}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(o) => o.product_name}
                    filterOptions={(x) => x}
                    inputValue={productInput}
                    onInputChange={(_, v, r) => r === 'input' && setProductInput(v)}
                    value={(productOptions || []).filter((p) => field.value?.includes(p.id))}
                    onChange={(_, v) => field.onChange(v.map((x) => x.id))}
                    renderInput={(p) => <TextField {...p} label="Products" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                name="promo_image"
                initialImage={promoImage}
                setValue={setValue}
                folderName={`images/telehealth/${user.clinic_id}`}
                maxFiles={1}
                label="Promo Image"
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <RHFImageUpload
                name="therapy_images"
                initialImage={therapyImages}
                setValue={setValue}
                folderName={`images/telehealth/${user.clinic_id}/gallery`}
                maxFiles={10}
                label="Therapy Images"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="prescription_required"
                    control={control}
                    render={({ field }) => (
                      <Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />
                    )}
                  />
                }
                label="Prescription Required"
              />
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" fullWidth sx={{ background: '#D92D7F' }}>
                Update Tele Health Therapy
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
